import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Collatz, Haskell and Memoization",
  "description": "",
  "published": "2012-01-09T00:00:00.000Z",
  "redirect_from": ["/blog/collatz-haskell-and-memoization/swizec/3382"],
  "categories": "collatzconjecture, Haskell, Languages, Math, Memoization, Number Theory, Programming, Project Euler, Uncategorized",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2012/01/collatz_conjecture3.png",
        "alt": "xkcd collatz conjecture",
        "title": "xkcd collatz conjecture"
      }}></img></p>
    <p>{`After an awesome `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Longboarding",
        "title": "Longboarding"
      }}>{`longboarding`}</a>{` session yesterday afternoon I decided to play around with `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Sequence",
        "title": "Sequence"
      }}>{`infinite sequences`}</a>{` in `}<a parentName="p" {...{
        "href": "http://haskell.org",
        "title": "Haskell (programming language)"
      }}>{`Haskell`}</a>{` - it's supposed to be one of the more (most?) powerful features of Haskell - because it's a `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Evaluation_strategy",
        "title": "Evaluation strategy"
      }}>{`lazy language`}</a>{` apparently.`}</p>
    <p>{`My first impulse of creating a primes generator was nipped in the bud by a long page of `}<a parentName="p" {...{
        "href": "http://www.haskell.org/haskellwiki/Prime_numbers"
      }}>{`prime number generators in Haskell`}</a>{`. Scary, complex, mindboggling.`}</p>
    <p><a parentName="p" {...{
        "href": "http://projecteuler.net/",
        "title": "Project Euler"
      }}>{`Project Euler`}</a>{` posed a much better challenge: `}<em parentName="p">{`Which starting number, under one million, produces the longest collatz chain?`}</em></p>
    <p>{`The solution I came up with was a simple brute force generator of infinitely many collatz sequences. Then I would take the first 1,000,000 find the maximum and that's that.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`collatz :: Integer -> [Integer]
collatz 1 = []
collatz n
    | odd n = 3*n+1:collatz(3*n+1)
    | even n = div n 2:collatz(div n 2)

chains = [collatz x | x <- [1..]]
`}</code></pre>
    <p>{`Didn't help.`}</p>
    <p>{`So I started looking for the maximum a bit differently - take all the sequences, sort them by length and take the last one.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`longest max =
  last $ sortBy (comparing snd) $ zip [1..] $ map length $ take max chains
`}</code></pre>
    <p>{`Great! It worked! But it takes ~26 seconds!`}</p>
    <p>{`Well sorting maybe isn't the best idea ever, so let's try creating a list of sequences where the list's tail only contains those sequences that are longer than the head. A sprinkle of dropWhile and it was done.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`longest' (max_i, max_l) =
  let l = head $ dropWhile (\\(i,l) -> l <= max_l) $ zip [max_i..] $ map length $ chains' max_i
  in l:longest' l
`}</code></pre>
    <p>{`~25 seconds!`}</p>
    <p>{`That's odd ... even odder still is running both algorithms one after another only takes 33 seconds. Huh?`}</p>
    <p>{`It would seem I'm using `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Memoization",
        "title": "Memoization"
      }}>{`memoization`}</a>{` incorrectly. I've heard it performs funny in recursive functions. The theory I formulated last night was that because haskell was lazy each execution chain was constructed to its end and the intermittent memoized values never got used until the whole function was called again.`}</p>
    <p>{`Looking at the code samples this morning, though, I discovered this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`collatz :: Integer -> [Integer]
collatz = memoize col where
  col 1 = []
  col n
    | odd n = 3*n+1:collatz(3*n+1)
    | even n = div n 2:collatz(div n 2)
`}</code></pre>
    <p>{`As you can see, I don't call the memoized function internally. Just goes to show what a night's sleep can do to one's coding abilities. I bashed my head against this problem for four hours yesterday and I never noticed I was recursing to the wrong function!`}</p>
    <p>{`Interestingly enough, fixing that makes the algorithm spaz out and die after 16 seconds. The only output I get is "Killed". Curious.`}</p>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://mrhonner.com/2011/11/18/graphing-the-collatz-conjecture/"
        }}>{`Graphing the Collatz Conjecture`}</a>{` (mrhonner.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://existentialtype.wordpress.com/2011/04/24/the-real-point-of-laziness/"
        }}>{`The Point of Laziness in Programing Languages`}</a>{` (existentialtype.wordpress.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://swizec.com/blog/learning-me-a-haskell/swizec/3272"
        }}>{`Learning me a Haskell`}</a>{` (swizec.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://geeklogs.posterous.com/what-is-memoization"
        }}>{`Memoization explained using a python implementation of fibonacci`}</a>{` (geeklogs.posterous.com)`}</li>
    </ul>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=6f7c0838-57e3-4b8a-a0c3-3ef0dc670559",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      